import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { supportedCountryCodes } from '@app/common/de-common';
import {
  availableCountryCodes,
  setCountryFlag,
} from '@app/common/select-country';
import { HeaderService } from '@app/service/header/header.service';
import { Subscription } from 'rxjs/internal/Subscription';
import {
  CheckoutStatus,
  StateService,
} from 'src/app/service/state/state.service';
import {
  EmailAndPhone,
  UserRegistrationDto,
} from 'src/app/service/user-service/user.service';
import { email_validation_regexp } from 'src/assets/val/regexp';
import {
  formatPhoneNumber,
  Item,
  phoneNumberValidator,
} from 'web-component-library/projects/component-library/src/public-api';
import { RouteComponent } from '../../route.component';
import { DeviceDetectorService } from 'ngx-device-detector';
import Translations from './fi-email-and-phone-registration.component.json';
import { FiUserService } from '@app/service/fi-user-service/fi-user.service';

enum View {
  DEFAULT = 'DEFAULT',
  NOT_FOUND = 'NOT_FOUND',
}

@Component({
  selector: 'app-fi-email-and-phone-registration',
  templateUrl: './fi-email-and-phone-registration.component.html',
  styleUrls: [
    '../../page-shared.css',
    './fi-email-and-phone-registration.component.scss',
  ],
})
export class FiEmailAndPhoneRegistrationComponent
  extends RouteComponent
  implements OnInit, OnDestroy
{
  form: FormGroup;
  loading: boolean = true;
  showError: boolean = false;
  currentView: View = View.DEFAULT;
  navigationInProgress: boolean = false;
  isMobile: boolean;
  preExistingContactInfo: UserRegistrationDto;
  View = View;
  readonly AVAILABLECOUNTRYCODES = availableCountryCodes;
  selectedCountry: Item = null;
  formSubscription: Subscription;

  translations: { [key: string]: string };

  constructor(
    private formBuilder: FormBuilder,
    protected router: Router,
    protected route: ActivatedRoute,
    protected stateService: StateService,
    protected headerService: HeaderService,
    private fiUserService: FiUserService,
    private deviceDetectorService: DeviceDetectorService
  ) {
    super(router, route, stateService, headerService);
  }

  async ngOnInit(): Promise<void> {
    this.translations =
      Translations[
        this.fiUserService.getCurrentStateLocaleBaseCode(this.state.language)
      ];
    await this.setup();
  }

  async setup(): Promise<void> {
    if (
      this.state.status === CheckoutStatus.EXPIRED ||
      this.state.status === CheckoutStatus.CANCELLED
    ) {
      return;
    }

    this.selectedCountry = this.AVAILABLECOUNTRYCODES[4];
    this.isMobile = this.deviceDetectorService.isMobile();
    this.preExistingContactInfo =
      await this.fiUserService.getActiveUserInformation();

    if (this.preExistingContactInfo) {
      this.preExistingContactInfo.phoneNumber.value = this.appendCountryCode(
        this.preExistingContactInfo.phoneNumber?.value || ''
      );

      this.selectedCountry = setCountryFlag(
        this.preExistingContactInfo.phoneNumber.value,
        this.selectedCountry
      );
    }

    this.form = this.formBuilder.group({
      email: [
        {
          value: this.preExistingContactInfo?.email.value || '',
          disabled: this.preExistingContactInfo?.email
            ? !this.preExistingContactInfo.email.editable
            : false,
        },
        [Validators.pattern(email_validation_regexp), Validators.required],
      ],
      phoneNumber: [
        {
          value:
            this.preExistingContactInfo?.phoneNumber.value ||
            this.selectedCountry?.value ||
            '',
          disabled: this.preExistingContactInfo?.phoneNumber
            ? !this.preExistingContactInfo.phoneNumber.editable
            : false,
        },
        [
          phoneNumberValidator(
            supportedCountryCodes,
            this.headerService.market
          ),
          Validators.required,
        ],
      ],
    });
    this.loading = false;

    this.formSubscription =
      this.form.controls.phoneNumber.valueChanges.subscribe((value) => {
        this.selectedCountry = setCountryFlag(value, this.selectedCountry);
      });
  }

  appendCountryCode(phoneNumber: string): string {
    if (
      phoneNumber?.length > 1 &&
      phoneNumber.slice(0, 1) !== '+' &&
      phoneNumber.slice(0, 2) !== '00'
    ) {
      return this.AVAILABLECOUNTRYCODES[4].value + phoneNumber;
    } else {
      return phoneNumber;
    }
  }

  ngOnDestroy(): void {
    this.formSubscription && this.formSubscription.unsubscribe();
  }

  setCountry(selected: Item): void {
    this.form.controls.phoneNumber.setValue(selected.value);
  }

  setCurrentView(view: View): void {
    this.currentView = view;
  }

  async handleCtaClick(): Promise<void> {
    this.navigationInProgress = true;

    try {
      if (this.form.valid) {
        const formData: EmailAndPhone = {
          email: this.form.controls.email.value.trim().toLowerCase(),
          phoneNumber: formatPhoneNumber(
            this.form.controls.phoneNumber.value,
            this.state.market
          ),
        };
        await this.fiUserService
          .registerEmailAndPhone(formData)
          .then(() => this.navigate());

        // TODO MAER: Requires special handling in the backend.
        // this.setCurrentView(View.NOT_FOUND);
      } else {
        this.showError = true;
      }
    } catch (error) {
      this.handleError(error);
    }

    this.navigationInProgress = false;
  }
}
